/* eslint-disable */
import React from "react";
import { SimpleGrid, Paper, Group, Text, Badge, Stack } from "@mantine/core";
import {
  IconCalendar,
  IconHexagon,
  IconCube,
  IconAlertCircle,
  IconStethoscope,
} from "@tabler/icons-react";
import { useGetLeaveBalance } from "api/newLeave/getLeavebalance";

type Card = {
  id: string;
  title: string;
  count: string;
  allocated: string;
  remaining: string | number;
  color: string;
  icon: React.ReactNode;
  circleBg?: string;
};

// Format number to always show 2 decimal places for consistency
const formatNumber = (num: any) => {
  if (num === null || num === undefined || num === "") return "0.00";
  const n = Number(num);

  if (isNaN(n)) return "0.00";

  // Always format to 2 decimal places
  return n.toFixed(2);
};

export default function LeaveSummaryCards({ userId }: { userId?: string }) {
  const { data } = useGetLeaveBalance(userId);
  const payload = data?.data;

  const getAllocated = (rec: any) =>
    Number(rec?.allocated ?? rec?.accrued ?? 0);
  const getUsed = (rec: any) => Number(rec?.used ?? 0);
  const getBalance = (rec: any) =>
    Number(
      rec?.balance ?? (rec?.allocated ?? rec?.accrued ?? 0) - (rec?.used ?? 0)
    );

  const getRawBalance = (rec: any) =>
    typeof rec?.balance !== "undefined"
      ? Number(rec.balance)
      : Number((rec?.allocated ?? rec?.accrued ?? 0) - (rec?.used ?? 0));

  const annual = payload?.annualLeave ?? null;
  const sick = payload?.sickLeave ?? null;
  console.log("Sick Leave Payload:", sick);
  const pressing = payload?.pressingNecessity ?? null;
  const lwp = payload?.leaveWithoutPay ?? null;
  const governance = payload?.governance ?? null;
  console.log("Governance Leave Payload test:", governance);

  const excludedKeys = new Set([
    "annualLeave",
    "sickLeave",
    "governance",
    "pressingNecessity",
    "leaveWithoutPay",
    "user",
    "org",
    "year",
    "_id",
    "lastUpdated",
    "createdAt",
    "updatedAt",
    "__v",
  ]);

  let otherCountSum = 0;
  let otherRemainingSum = 0;

  if (payload) {
    Object.keys(payload).forEach((key) => {
      if (!excludedKeys.has(key)) {
        const rec = (payload as any)[key];
        if (rec && typeof rec === "object") {
          otherCountSum += getAllocated(rec);
          otherRemainingSum += getBalance(rec);
        }
      }
    });
  }
  const lightBg = (hex: string, opacity = 0.12) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const cards: Card[] = [
    {
      id: "annual",
      title: "Annual Leaves",
      count: formatNumber(getAllocated(annual)),
      allocated: formatNumber(getAllocated(annual)),
      remaining: formatNumber(getBalance(annual)),
      color: "#7950f2",
      icon: <IconCalendar size={20} stroke={2} />,
      circleBg: lightBg("#7950f2"),
    },
    {
      id: "medical",
      title: "Medical Leaves",
      count: formatNumber(getAllocated(sick)),
      allocated: formatNumber(getAllocated(sick)),
      remaining: formatNumber(getBalance(sick)),
      color: "#7950f2",
      icon: <IconStethoscope size={20} stroke={2} />,
      circleBg: lightBg("#7950f2"),
    },
    {
      id: "casual",
      title: "Pressing Necessity",
      count: formatNumber(getAllocated(pressing)),
      allocated: formatNumber(getAllocated(pressing)),
      remaining: formatNumber(getBalance(pressing)),
      color: "#7950f2",
      icon: <IconHexagon size={20} stroke={2} />,
      circleBg: lightBg("#7950f2"),
    },
    {
      id: "governance",
      title: "Governance Leave",
      count: formatNumber(getAllocated(governance)),
      allocated: formatNumber(getAllocated(governance)),
      remaining: formatNumber(getBalance(governance)),
      color: "#7950f2",
      icon: <IconCube size={20} stroke={2} />,
      circleBg: lightBg("#7950f2"),
    },
    {
      id: "leaveWithoutPay",
      title: "Leave Without Pay",
      count: formatNumber(getUsed(lwp)),
      allocated: formatNumber(getAllocated(lwp)),
      remaining: formatNumber(getBalance(lwp)),
      color: "#7950f2",
      icon: <IconAlertCircle size={20} stroke={2} />,
      circleBg: lightBg("#7950f2"),
    },

    // {
    //   id: "other",
    //   title: "Other Leaves",
    //   count: formatNumber(otherCountSum),
    //   remaining: formatNumber(otherRemainingSum),
    //   color: "#FF4DA6",
    //   icon: <IconCube size={20} stroke={2} />,
    //   circleBg: "rgba(255,77,166,0.12)",
    // },
  ];

  return (
    <SimpleGrid
      cols={4}
      spacing="lg"
      breakpoints={[
        { maxWidth: 1045, cols: 2 },
        { maxWidth: 600, cols: 1 },
      ]}
    >
      {cards.map((c) => (
        <Paper
          key={c.id}
          shadow="xs"
          radius="md"
          p="lg"
          withBorder
          sx={(theme) => ({
            position: "relative",
            overflow: "hidden",
            background:
              theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
          })}
        >
          <div
            style={{
              position: "absolute",
              right: "-28px",
              top: "-18px",
              width: "160px",
              height: "160px",
              borderRadius: 999,
              background: c.circleBg || "rgba(0,0,0,0.06)",
            }}
          />

          <div
            style={{
              position: "absolute",
              right: "14px",
              top: "12px",
              width: "64px",
              height: "64px",
              borderRadius: 999,
              background: c.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
            }}
          >
            <div
              style={{
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {c.icon}
            </div>
          </div>

          <Stack spacing={6} sx={{ height: "100%" }}>
            <Group position="apart" align="flex-start">
              <div>
                <Text size="sm" color="dimmed">
                  {c.title}
                </Text>

                <Text weight={700} size="xl" mt={6}>
                  {c.count}
                </Text>

                <div style={{ marginTop: 10 }}>
                  <Badge
                    variant="light"
                    radius="sm"
                    sx={{
                      background: (() => {
                        try {
                          const hex = c.color.replace("#", "");
                          const r = parseInt(hex.substring(0, 2), 16);
                          const g = parseInt(hex.substring(2, 4), 16);
                          const b = parseInt(hex.substring(4, 6), 16);
                          return `rgba(${r}, ${g}, ${b}, 0.08)`;
                        } catch (err) {
                          return undefined;
                        }
                      })(),
                      color: c.color,
                      padding: "6px 10px",
                      fontWeight: 600,
                      fontSize: "12px",
                    }}
                  >
                    Balance: {c.remaining}
                  </Badge>
                </div>
              </div>
            </Group>
          </Stack>
        </Paper>
      ))}
    </SimpleGrid>
  );
}
